import { useCallback, useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import type { IOpcaoProdutoFiltroCobranca } from "@/hooks/cobranca-credito-cliente/buscar-cobranca-por-cliente";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import { EXTRATO_CREDITO_QUERY_STALE_TIME } from "@/common/const/query-cache";
import { handleMinhaCobrancaCreditos } from "@/domain/use-cases/vendas-creditos";
import type {
  IFiltrosCobrancaCreditoCliente,
  IParametrosConsultaCobrancaCreditoCliente,
  IRespostaMinhaCobrancaCredito,
} from "@/interface/extrato-credito-cliente";
import { obterIntervaloMesAtual } from "@/utils/credito/obter-intervalo-mes-atual";

const ITENS_POR_PAGINA_PADRAO_MINHA_COBRANCA = 10;

function obterFiltrosMesAtual(): IFiltrosCobrancaCreditoCliente {
  const { dataInicio, dataFim } = obterIntervaloMesAtual();
  return { dataInicio, dataFim };
}

/**
 * Cobrança de créditos do cliente logado (GET vendas_creditos/minha-cobranca).
 * O cliente vem do token; a resposta não traz valores em R$.
 */
export function useMinhaCobrancaCreditos({ habilitado }: { habilitado: boolean }) {
  const { token: accessToken } = useStoreZustandUserAuth();

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [filtros, setFiltros] = useState<IFiltrosCobrancaCreditoCliente>(obterFiltrosMesAtual);
  const [opcoesProduto, setOpcoesProduto] = useState<IOpcaoProdutoFiltroCobranca[]>([]);

  const parametrosConsulta: IParametrosConsultaCobrancaCreditoCliente = useMemo(
    () => ({
      paginaAtual,
      itensPorPagina: ITENS_POR_PAGINA_PADRAO_MINHA_COBRANCA,
      produtoIdFiltro: filtros.produtoId,
      dataInicioFiltro: filtros.dataInicio,
      dataFimFiltro: filtros.dataFim,
    }),
    [paginaAtual, filtros]
  );

  const {
    data: resposta,
    isLoading: carregando,
    isFetching: buscando,
    isError: erro,
  } = useQuery<IRespostaMinhaCobrancaCredito | null>({
    queryKey: ["minha-cobranca-creditos", parametrosConsulta],
    enabled: Boolean(habilitado && accessToken),
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      if (!accessToken) {
        return null;
      }

      const resultado = await handleMinhaCobrancaCreditos(accessToken, parametrosConsulta);

      if (resultado && typeof resultado === "object" && "totais" in resultado) {
        return resultado as IRespostaMinhaCobrancaCredito;
      }

      throw resultado;
    },
    staleTime: EXTRATO_CREDITO_QUERY_STALE_TIME,
  });

  const paginacao = resposta?.pagination;
  const totaisPorProduto = resposta?.totais_por_produto ?? [];

  // As opções do filtro de produto vêm dos próprios totais: acumula os produtos já vistos
  // para o filtro não "sumir" com os demais quando um produto estiver selecionado.
  useEffect(() => {
    if (!resposta?.totais_por_produto?.length) {
      return;
    }
    setOpcoesProduto((anteriores) => {
      const porId = new Map(anteriores.map((opcao) => [opcao.produtoId, opcao]));
      for (const item of resposta.totais_por_produto) {
        porId.set(Number(item.produto_id), {
          produtoId: Number(item.produto_id),
          nome: item.produto_nome,
        });
      }
      return [...porId.values()].sort((a, b) => a.nome.localeCompare(b.nome));
    });
  }, [resposta]);

  useEffect(() => {
    const ultimaPagina = paginacao?.last_page;
    if (ultimaPagina != null && ultimaPagina > 0 && paginaAtual > ultimaPagina) {
      setPaginaAtual(ultimaPagina);
    }
  }, [paginacao?.last_page, paginaAtual]);

  const definirPaginaAtual = useCallback((novaPagina: number) => {
    setPaginaAtual(Math.max(1, novaPagina));
  }, []);

  const definirFiltros = useCallback((patch: Partial<IFiltrosCobrancaCreditoCliente>) => {
    setFiltros((anterior) => ({ ...anterior, ...patch }));
    setPaginaAtual(1);
  }, []);

  const limparFiltros = useCallback(() => {
    setFiltros({});
    setPaginaAtual(1);
  }, []);

  const aplicarFiltroMesAtual = useCallback(() => {
    definirFiltros(obterFiltrosMesAtual());
  }, [definirFiltros]);

  return {
    linhas: resposta?.data ?? [],
    paginacao,
    totais: resposta?.totais,
    totaisPorProduto,
    carregando,
    buscando,
    erro,
    paginaAtual,
    itensPorPagina: ITENS_POR_PAGINA_PADRAO_MINHA_COBRANCA,
    definirPaginaAtual,
    filtros,
    definirFiltros,
    limparFiltros,
    aplicarFiltroMesAtual,
    opcoesProduto,
  };
}
